import React, { useState } from 'react';
import Icon from '../AppIcon';

const FORMATS = [
  {
    id: 'pdf',
    label: 'PDF Preview',
    description: 'Opens a formatted report you can print or save as PDF',
    icon: 'FileSearch',
  },
  {
    id: 'csv',
    label: 'CSV',
    description: 'Opens in Excel, Google Sheets, or any spreadsheet app',
    icon: 'FileText',
  },
  {
    id: 'json',
    label: 'JSON',
    description: 'Raw structured data, useful for further processing',
    icon: 'Code',
  },
];

const DATE_RANGE_LABELS = {
  upcoming:   'Upcoming',
  today:      'Today',
  past:       'Past',
  this_week:  'This week',
  this_month: 'This month',
};

const ExportReportModal = ({ isOpen, onClose, onExport, recordCount, title = 'Report', filters = {} }) => {
  const [format, setFormat] = useState('pdf');

  if (!isOpen) return null;

  const summaryParts = [];
  if (filters.dateRange) {
    summaryParts.push(
      filters.dateRange === 'custom' && (filters.startDate || filters.endDate)
        ? `${filters.startDate || '…'} – ${filters.endDate || '…'}`
        : (DATE_RANGE_LABELS[filters.dateRange] ?? filters.dateRange)
    );
  }
  summaryParts.push(
    filters.status && filters.status !== 'all'
      ? filters.status.charAt(0).toUpperCase() + filters.status.slice(1)
      : 'All statuses'
  );

  const isPdf = format === 'pdf';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md">

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-5">
          <div>
            <h2 className="font-heading font-bold text-xl text-foreground leading-tight">
              Export {title}
            </h2>
            <p className="text-sm text-foreground/60 mt-1 font-caption">
              Choose a format to export your data
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground/50 hover:bg-muted hover:text-foreground transition-colors ml-4 flex-shrink-0"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-5">

          {/* Summary */}
          <div className="bg-muted/70 border border-border rounded-xl px-4 py-3.5 flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'var(--color-primary)', opacity: 0.9 }}
            >
              <Icon name="BarChart2" size={16} color="white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground font-caption">
                {recordCount} {recordCount === 1 ? 'record' : 'records'}
              </p>
              <p className="text-sm text-foreground/60 font-caption mt-0.5">
                {summaryParts.join(' · ')}
              </p>
            </div>
          </div>

          {/* Format label */}
          <div>
            <p className="text-xs font-bold text-foreground/50 uppercase tracking-widest mb-3 font-caption">
              Format
            </p>

            <div className="space-y-2">
              {FORMATS.map(f => (
                <button
                  key={f.id}
                  onClick={() => setFormat(f.id)}
                  className={`w-full flex items-center gap-3.5 px-4 py-3 rounded-xl border text-left transition-all ${
                    format === f.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/40 hover:bg-muted/60'
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                      format === f.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground/50'
                    }`}
                  >
                    <Icon name={f.icon} size={16} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground font-caption">{f.label}</p>
                    <p className="text-[12px] text-foreground/55 font-caption mt-0.5 leading-snug">
                      {f.description}
                    </p>
                  </div>

                  <div
                    className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                      format === f.id ? 'border-primary bg-primary' : 'border-border'
                    }`}
                  >
                    {format === f.id && (
                      <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors font-caption"
            >
              Cancel
            </button>
            <button
              onClick={() => { onExport(format); onClose(); }}
              className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity font-caption flex items-center justify-center gap-2"
            >
              <Icon name={isPdf ? 'ExternalLink' : 'Download'} size={15} />
              {isPdf ? 'Preview' : 'Download'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ExportReportModal;
